import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  GUESTERIA_PARTNER_DESTINATION_TYPE_OPTIONS,
  GUESTERIA_PARTNER_CONNECTION_OPTIONS,
  countActiveGuesteriaFilters,
  DEFAULT_GUESTERIA_PARTNERS_FILTERS,
  type GuesteriaPartnersFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFilters";
import { GuesteriaPartnersFilterButton } from "@/components/pages/guesteria-partners/components/GuesteriaPartnersFilterButton";
import type {
  GuesteriaPartnerDestinationType,
  GuesteriaPartnerConnectionStatus,
} from "@/components/pages/guesteria-partners/utils/guesteriaPartnersModel";
import { cn } from "@/lib/utils";

const GUESTERIA_FILTER_CHECKBOX_CLASS =
  "size-5 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-[#30344A] data-[state=checked]:bg-[#30344A] data-[state=checked]:text-white";

const GUESTERIA_FILTER_COMBOBOX_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-[#737373] outline-none transition-colors hover:bg-zinc-50/80 focus-visible:ring-0";

export type GuesteriaPartnersFiltersPopoverProps = {
  applied: GuesteriaPartnersFiltersState;
  onApply: (next: GuesteriaPartnersFiltersState) => void;
};

export function GuesteriaPartnersFiltersPopover({
  applied,
  onApply,
}: GuesteriaPartnersFiltersPopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] =
    React.useState<GuesteriaPartnersFiltersState>(applied);
  const [destinationTypeOpen, setDestinationTypeOpen] = React.useState(true);
  const [destinationTypeSearch, setDestinationTypeSearch] = React.useState("");
  const [connectionOpen, setConnectionOpen] = React.useState(false);

  const activeCount = countActiveGuesteriaFilters(draft);

  const visibleDestinationTypeOptions = React.useMemo(() => {
    const q = destinationTypeSearch.trim().toLowerCase();
    if (!q) {
      return GUESTERIA_PARTNER_DESTINATION_TYPE_OPTIONS;
    }
    return GUESTERIA_PARTNER_DESTINATION_TYPE_OPTIONS.filter(
      (destinationType) =>
        t(`partners.table.destinationTypeValues.${destinationType}`)
          .toLowerCase()
          .includes(q),
    );
  }, [destinationTypeSearch, t]);

  const syncDraftFromApplied = React.useCallback(() => {
    setDraft(applied);
    setDestinationTypeSearch("");
    setDestinationTypeOpen(true);
    setConnectionOpen(applied.connectionStatuses.length > 0);
  }, [applied]);

  const toggleDestinationType = (
    destinationType: GuesteriaPartnerDestinationType,
  ) => {
    setDraft((prev) => {
      const has = prev.destinationTypes.includes(destinationType);
      const destinationTypes = has
        ? prev.destinationTypes.filter((c) => c !== destinationType)
        : [...prev.destinationTypes, destinationType];
      return { ...prev, destinationTypes };
    });
  };

  const removeDestinationTypeChip = (
    destinationType: GuesteriaPartnerDestinationType,
  ) => {
    setDraft((prev) => ({
      ...prev,
      destinationTypes: prev.destinationTypes.filter(
        (c) => c !== destinationType,
      ),
    }));
  };

  const toggleConnection = (status: GuesteriaPartnerConnectionStatus) => {
    setDraft((prev) => {
      const has = prev.connectionStatuses.includes(status);
      const connectionStatuses = has
        ? prev.connectionStatuses.filter((s) => s !== status)
        : [...prev.connectionStatuses, status];
      return { ...prev, connectionStatuses };
    });
  };

  const removeConnectionChip = (status: GuesteriaPartnerConnectionStatus) => {
    setDraft((prev) => ({
      ...prev,
      connectionStatuses: prev.connectionStatuses.filter((s) => s !== status),
    }));
  };

  const clearSearch = () => {
    setDraft((prev) => ({ ...prev, searchQuery: "" }));
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          syncDraftFromApplied();
        }
      }}
    >
      <PopoverTrigger asChild>
        <GuesteriaPartnersFilterButton />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex max-h-[min(560px,calc(100vh-120px))] w-[320px] flex-col gap-6 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="flex shrink-0 flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-base leading-normal font-semibold text-[#171717]">
              {activeCount > 0
                ? t("partners.table.filterPopover.titleWithCount", {
                    count: activeCount,
                  })
                : t("partners.table.filterPopover.title")}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label={t("partners.table.filterPopover.closeAria")}
            >
              <X className="size-5" />
            </Button>
          </div>

          {activeCount > 0 ? (
            <div className="flex flex-wrap gap-2">
              {draft.searchQuery.trim() ? (
                <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]">
                  <span className="truncate">{draft.searchQuery.trim()}</span>
                  <Button
                    type="button"
                    variant="span"
                    className="rounded p-0.5 hover:bg-[#DDD6FE]"
                    onClick={clearSearch}
                    aria-label={t(
                      "partners.table.filterPopover.clearSearchAria",
                    )}
                  >
                    <X className="size-3.5 shrink-0" />
                  </Button>
                </span>
              ) : null}
              {draft.destinationTypes.map((destinationType) => (
                <span
                  key={destinationType}
                  className="inline-flex items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]"
                >
                  {t(`partners.table.destinationTypeValues.${destinationType}`)}
                  <Button
                    type="button"
                    variant="span"
                    className="rounded p-0.5 hover:bg-[#DDD6FE]"
                    onClick={() => removeDestinationTypeChip(destinationType)}
                    aria-label={t("partners.table.filterPopover.closeAria")}
                  >
                    <X className="size-3.5 shrink-0" />
                  </Button>
                </span>
              ))}
              {draft.connectionStatuses.map((status) => (
                <span
                  key={status}
                  className="inline-flex items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]"
                >
                  {t(`partners.table.connectionStatusValues.${status}`)}
                  <Button
                    type="button"
                    variant="span"
                    className="rounded p-0.5 hover:bg-[#DDD6FE]"
                    onClick={() => removeConnectionChip(status)}
                    aria-label={t("partners.table.filterPopover.closeAria")}
                  >
                    <X className="size-3.5 shrink-0" />
                  </Button>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-0.5">
          <div className="relative shrink-0">
            <Input
              type="search"
              icon={<Search className="size-4 text-[#737373]" />}
              placeholder={t("partners.table.filterPopover.searchPlaceholder")}
              value={draft.searchQuery}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, searchQuery: e.target.value }))
              }
              className="h-9 border-[#E5E5E5] pr-9 text-sm text-[#171717] shadow-none placeholder:text-[#737373] focus-visible:border-[#E5E5E5] focus-visible:ring-0"
            />
            {draft.searchQuery ? (
              <Button
                type="button"
                variant="span"
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-[#737373] hover:bg-zinc-100 hover:text-[#171717]"
                onClick={clearSearch}
                aria-label={t("partners.table.filterPopover.clearSearchAria")}
              >
                <X className="size-4" />
              </Button>
            ) : null}
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              variant="span"
              className={GUESTERIA_FILTER_COMBOBOX_TRIGGER_CLASS}
              onClick={() => setDestinationTypeOpen((v) => !v)}
              aria-expanded={destinationTypeOpen}
            >
              {t("partners.table.filterPopover.destinationType")}
              <ChevronDown
                className={cn(
                  "size-4 shrink-0 text-[#737373] transition-transform",
                  destinationTypeOpen && "rotate-180",
                )}
              />
            </Button>

            {destinationTypeOpen ? (
              <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
                <div className="relative border-b border-[#E5E5E5] p-2">
                  <Input
                    type="search"
                    icon={<Search className="size-4 text-[#737373]" />}
                    placeholder={t(
                      "partners.table.filterPopover.destinationTypeSearchPlaceholder",
                    )}
                    value={destinationTypeSearch}
                    onChange={(event) =>
                      setDestinationTypeSearch(event.target.value)
                    }
                    className="h-8 border-[#E5E5E5] pr-8 text-sm shadow-none focus-visible:ring-0"
                  />
                  {destinationTypeSearch ? (
                    <Button
                      type="button"
                      variant="span"
                      className="absolute top-1/2 right-4 -translate-y-1/2 rounded p-1 text-[#737373] hover:bg-zinc-100 hover:text-[#171717]"
                      onClick={() => setDestinationTypeSearch("")}
                      aria-label={t(
                        "partners.table.filterPopover.clearDestinationTypeSearchAria",
                      )}
                    >
                      <X className="size-3.5" />
                    </Button>
                  ) : null}
                </div>
                <div className="max-h-[176px] overflow-y-auto p-1">
                  {visibleDestinationTypeOptions.length === 0 ? (
                    <p className="px-2 py-1.5 text-sm text-[#737373]">
                      {t(
                        "partners.table.filterPopover.noDestinationTypesFound",
                      )}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {visibleDestinationTypeOptions.map((destinationType) => {
                        const checked =
                          draft.destinationTypes.includes(destinationType);
                        return (
                          <label
                            key={destinationType}
                            className={cn(
                              "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
                              checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() =>
                                toggleDestinationType(destinationType)
                              }
                              className={GUESTERIA_FILTER_CHECKBOX_CLASS}
                            />
                            <span className="text-sm leading-normal font-normal text-[#171717]">
                              {t(
                                `partners.table.destinationTypeValues.${destinationType}`,
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              variant="span"
              className={GUESTERIA_FILTER_COMBOBOX_TRIGGER_CLASS}
              onClick={() => setConnectionOpen((v) => !v)}
              aria-expanded={connectionOpen}
            >
              {t("partners.table.filterPopover.connectionStatus")}
              <ChevronDown
                className={cn(
                  "size-4 shrink-0 text-[#737373] transition-transform",
                  connectionOpen && "rotate-180",
                )}
              />
            </Button>

            {connectionOpen ? (
              <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
                <div className="max-h-[176px] overflow-y-auto p-1">
                  <div className="space-y-1">
                    {GUESTERIA_PARTNER_CONNECTION_OPTIONS.map((status) => {
                      const checked = draft.connectionStatuses.includes(status);
                      return (
                        <label
                          key={status}
                          className={cn(
                            "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
                            checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                          )}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleConnection(status)}
                            className={GUESTERIA_FILTER_CHECKBOX_CLASS}
                          />
                          <span className="text-sm leading-normal font-normal text-[#171717]">
                            {t(
                              `partners.table.connectionStatusValues.${status}`,
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => {
              setDraft(DEFAULT_GUESTERIA_PARTNERS_FILTERS);
              setDestinationTypeSearch("");
              setDestinationTypeOpen(true);
              setConnectionOpen(false);
              onApply(DEFAULT_GUESTERIA_PARTNERS_FILTERS);
            }}
          >
            {t("partners.table.filterPopover.reset")}
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={() => {
              onApply(draft);
              setOpen(false);
            }}
          >
            {t("partners.table.filterPopover.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
